// Pure parser for the special-property encoding used in model element data stores.
// Format: [label](TYPE|data) — where TYPE is DOC, LINK, VIEW, or a bare text value.
// Cross-workspace: duplicated intentionally from the viewer (cf. isValidJwtFormat in
// app + admin). The viewer is the source of truth for the encoding.

export type PropertyType = 'DOC' | 'LINK' | 'VIEW' | 'TEXT';

export interface ParsedProperty {
  type: PropertyType;
  label: string;
  /** Fully-formed URL for DOC (redirect endpoint) and LINK types. */
  url?: string;
  /** Original filename for DOC type. */
  filename?: string;
}

// Matches [label](rest) — the markdown-link encoding the viewer uses.
const MARKDOWN_RE = /^\[(.*)\]\((.*)\)$/;

/**
 * Parse a raw property value string into its structured representation.
 *
 * Supported formats:
 *   DOC|<url>|<filename>          → { type: 'DOC', label: filename, url, filename }
 *   LINK|<url>|<label>            → { type: 'LINK', label, url }
 *   VIEW|<camera-params>          → { type: 'VIEW', label: value }
 *   [label](DOC|<url>|<filename>) → same as DOC above (markdown wrapper)
 *   [label](LINK|<url>)           → same as LINK above (markdown wrapper)
 *   [label](VIEW|...)             → same as VIEW above (markdown wrapper)
 *   anything else                 → { type: 'TEXT', label: value }
 */
export function parseSpecialProperty(value: string): ParsedProperty {
  if (!value) return { type: 'TEXT', label: value };

  // Unwrap markdown [label](inner) if present; otherwise use value directly.
  const match = MARKDOWN_RE.exec(value);
  const inner = match ? match[2] : value;

  if (inner.startsWith('DOC|')) {
    const rest = inner.slice(4);
    const pipe = rest.indexOf('|');
    if (pipe !== -1) {
      const url = rest.slice(0, pipe);
      const filename = rest.slice(pipe + 1);
      if (url && filename) {
        return { type: 'DOC', label: filename, url, filename };
      }
    }
    // Malformed DOC — fall through to TEXT
    return { type: 'TEXT', label: value };
  }

  if (inner.startsWith('LINK|')) {
    const rest = inner.slice(5);
    const pipe = rest.indexOf('|');
    if (pipe !== -1) {
      const url = rest.slice(0, pipe);
      const label = rest.slice(pipe + 1) || url;
      if (url) {
        return { type: 'LINK', label, url };
      }
    }
    // LINK with no label separator — treat the whole rest as url
    if (rest) {
      return { type: 'LINK', label: rest, url: rest };
    }
    return { type: 'TEXT', label: value };
  }

  if (inner.startsWith('VIEW|')) {
    return { type: 'VIEW', label: value };
  }

  // Plain text or unrecognised format
  return { type: 'TEXT', label: value };
}
